import React, { useEffect, useMemo, useState } from "react";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { Link, NavLink } from "react-router-dom";
import { FaBars } from "react-icons/fa6";
import { IoClose } from "react-icons/io5";
import "./Navbar.scss";
import Cart from "../Cart/Cart";
import { useSelector } from "react-redux";
import { useLanguage } from "../../hooks/LanguageContext";

const Navbar = () => {
  const [menuOpen, setMenuOpen]       = useState(false); // mobile slide menu
  const [cartOpen, setCartOpen]       = useState(false); // cart drawer
  const [showLang, setShowLang]       = useState(false); // language dropdown
  const [showRelease, setShowRelease] = useState(false); // new release sub-menu

  const cartProducts = useSelector((state) => state.cart.products);
  const [savedOfferProduct, setSavedOfferProduct] = useState(null);
  const [proStatus, setProStatus] = useState(null);

  useEffect(() => {
    const stored = sessionStorage.getItem("offerProduct");
    if (stored) {
      try { setSavedOfferProduct(JSON.parse(stored)); } catch (e) {}
    }
    setProStatus(sessionStorage.getItem("status"));
  }, []);

  const products = useMemo(() => {
    if (proStatus === "new" && savedOfferProduct) {
      return [...cartProducts, savedOfferProduct];
    }
    return cartProducts;
  }, [cartProducts, savedOfferProduct, proStatus]);

  const { language, setLanguage } = useLanguage();

  const languageOptions = {
    en: { name: "English",  flag: require("../../img/5111640.png") },
    ar: { name: "عربي",     flag: require("../../img/206719.png")  },
    ru: { name: "Русский",  flag: require("../../img/206604.png")  },
    ja: { name: "日本語",   flag: require("../../img/japan_flag.jpg") },
    ko: { name: "한국어",   flag: require("../../img/korea_flag.png") },
    hi: { name: "हिंदी",   flag: require("../../img/india_flag.png") },
    zh: { name: "中文",     flag: require("../../img/china_flag.png") },
  };

  const content = {
    en: { shop: "Shop", newRelease: "New Release", kunafa: "Kunafa", gourmet: "Gourmet", vegan: "Vegan", bites: "Bites", about: "About", contact: "Contact" },
    ar: { shop: "تسوق", newRelease: "إصدار جديد", kunafa: "كنافة", gourmet: "جورميه", vegan: "نباتي", bites: "لقيمات", about: "حول", contact: "اتصل" },
    ru: { shop: "Магазин", newRelease: "Новый выпуск", kunafa: "Кунафа", gourmet: "Гурме", vegan: "Веганский", bites: "Закуски", about: "О нас", contact: "Контакт" },
    ja: { shop: "ショップ", newRelease: "新商品", kunafa: "クナファ", gourmet: "グルメ", vegan: "ビーガン", bites: "軽食", about: "について", contact: "お問い合わせ" },
    ko: { shop: "상점", newRelease: "신상품", kunafa: "쿠나파", gourmet: "미식", vegan: "비건", bites: "간식", about: "소개", contact: "연락처" },
    hi: { shop: "शॉप", newRelease: "नई रिहाई", kunafa: "कुनाफा", gourmet: "गौरमेट", vegan: "वीगन", bites: "नाश्ता", about: "बारे में", contact: "संपर्क करें" },
    zh: { shop: "商店", newRelease: "新发布", kunafa: "库纳法", gourmet: "美食", vegan: "素食", bites: "小吃", about: "关于", contact: "联系我们" },
  };

  const t = content[language];

  const closeAll = () => {
    setMenuOpen(false);
    setShowRelease(false);
    setShowLang(false);
  };

  return (
    <div className="__fw">
      <div className="__mw">
        <nav className="NavbarItems">

          {/* ── Logo ───────────────────── */}
          <div className="Logo_wrp">
            <Link to="/" onClick={closeAll}>
              <img className="logo_img" src={require("../../img/logo.webp")} alt="Chocodate" />
            </Link>
          </div>

          {/* ── Mobile: cart + hamburger ─ */}
          <div className="mobile_actions">
            <div className="cartIcon" onClick={() => setCartOpen(!cartOpen)}>
              <ShoppingCartIcon fontSize="small" />
              {products.length > 0 && <span>{products.length}</span>}
            </div>
            {menuOpen
              ? <IoClose size={26} onClick={() => setMenuOpen(false)} />
              : <FaBars  size={22} onClick={() => setMenuOpen(true)}  />
            }
          </div>

          {/* ── Nav menu ───────────────── */}
          <ul className={menuOpen ? "MenuItems active" : "MenuItems"}>

            <NavLink
              to="/products/1"
              onClick={closeAll}
              className={({ isActive }) => isActive ? "nv active" : "nv"}
            >
              <li>{t.shop}</li>
            </NavLink>

            {/* New Release with sub-menu */}
            <li className="nv has-dropdown" onClick={() => setShowRelease(!showRelease)}>
              <span>{t.newRelease} <em>▾</em></span>
              {showRelease && (
                <ul className="sub-menu">
                  <NavLink to="/kunafa"  onClick={closeAll}><li>{t.kunafa}</li></NavLink>
                  <NavLink to="/gourmet" onClick={closeAll}><li>{t.gourmet}</li></NavLink>
                  <NavLink to="/vegan"   onClick={closeAll}><li>{t.vegan}</li></NavLink>
                  <NavLink to="/bites"   onClick={closeAll}><li>{t.bites}</li></NavLink>
                </ul>
              )}
            </li>

            <NavLink
              to="/about"
              onClick={closeAll}
              className={({ isActive }) => isActive ? "nv active" : "nv"}
            >
              <li>{t.about}</li>
            </NavLink>

            <NavLink
              to="/contact"
              onClick={closeAll}
              className={({ isActive }) => isActive ? "nv active" : "nv"}
            >
              <li>{t.contact}</li>
            </NavLink>

            {/* Language selector */}
            <li className="nv lang-li">
              <button className="lang-btn" onClick={(e) => { e.stopPropagation(); setShowLang(!showLang); }}>
                <img src={languageOptions[language]?.flag} alt="" className="flag-icon" />
                <span>{languageOptions[language]?.name}</span>
              </button>
              {showLang && (
                <ul className="lang-dropdown" onClick={(e) => e.stopPropagation()}>
                  {Object.entries(languageOptions).map(([lang, data]) => (
                    <li key={lang} onClick={() => { setLanguage(lang); setShowLang(false); }}>
                      <img src={data.flag} alt="" className="flag-icon" />
                      {data.name}
                    </li>
                  ))}
                </ul>
              )}
            </li>

            {/* Cart icon (desktop only — mobile has it in mobile_actions) */}
            <li className="cart-desktop nv">
              <div className="cartIcon" onClick={() => setCartOpen(!cartOpen)}>
                <ShoppingCartIcon fontSize="small" />
                {products.length > 0 && <span>{products.length}</span>}
              </div>
            </li>

          </ul>
        </nav>

        {cartOpen && <Cart close={() => setCartOpen(false)} />}
      </div>
    </div>
  );
};

export default Navbar;
